// ============================================
// FILE: src/services/azureConfigHelper.js
// Export dummy constants + secure config object
// UPDATED: Added Azure Speech dummy constants
// ============================================

// ✅ AZURE OPENAI - Dummy values, never used for real API calls
export const AZURE_OPENAI_ENDPOINT = "SECURE_API";
export const AZURE_OPENAI_API_KEY = "SECURE_API";
export const AZURE_OPENAI_DEPLOYMENT = "SECURE_API";

// ✅ AZURE SPEECH - Dummy values, never used for real API calls
export const AZURE_SPEECH_KEY = "SECURE_API";
export const AZURE_SPEECH_REGION = "SECURE_API";

// ✅ HUME - Dummy values, never used for real API calls
export const HUME_API_KEY = "SECURE_API";

/**
 * Returns Azure OpenAI config that routes everything through SecureAPI
 * Contains dummy values but flags as secure
 */
export function getAzureConfig() {
  return {
    endpoint: AZURE_OPENAI_ENDPOINT,
    deployment: AZURE_OPENAI_DEPLOYMENT,
    apiKey: AZURE_OPENAI_API_KEY,
    
    // Flag so functions know to use SecureAPI instead
    _isSecure: true
  };
}

/**
 * Returns Azure Speech config that routes everything through SecureAPI
 * Contains dummy values but flags as secure
 */
export function getAzureSpeechConfig() {
  return {
    key: AZURE_SPEECH_KEY,
    region: AZURE_SPEECH_REGION,
    
    // Flag so functions know to use SecureAPI instead
    _isSecure: true,
    _useTokenAuth: true  // Indicates token-based auth
  };
}

/**
 * Returns Hume config that routes everything through SecureAPI
 * Contains dummy values but flags as secure
 */
export function getHumeConfig() {
  return {
    apiKey: HUME_API_KEY,
    
    // Flag so functions know to use SecureAPI instead
    _isSecure: true
  };
}

/**
 * Helper to check if config uses SecureAPI
 */
export function isSecureConfig(config) {
  return config?._isSecure === true;
}

/**
 * Helper to check if config uses token-based authentication
 */
export function usesTokenAuth(config) {
  return config?._useTokenAuth === true;
}

/**
 * Returns all secure configs in one object
 * Useful for initializing services
 */
export function getAllSecureConfigs() {
  return {
    azureOpenAI: getAzureConfig(),
    azureSpeech: getAzureSpeechConfig(),
    hume: getHumeConfig(),
    
    // Metadata
    isFullySecure: true,
    configVersion: "2.0.0"
  };
}

/**
 * Validates that no real API keys are present in config
 * Throws error if real keys detected
 */
export function validateSecureConfig(config) {
  const dangerousPatterns = [
    /^[A-Za-z0-9]{32,}$/,  // Long alphanumeric strings (API keys)
    /^sk-/,                 // OpenAI-style keys
    /^[A-Za-z0-9-_]{100,}$/,// Very long tokens
    /api\.openai\.com/,     // Direct OpenAI endpoints
    /cognitive\.microsoft/, // Direct Azure endpoints
  ];
  
  const checkValue = (value, path = '') => {
    if (typeof value === 'string') {
      // Skip secure placeholder values
      if (value === 'SECURE_API') return;
      
      // Check for dangerous patterns
      for (const pattern of dangerousPatterns) {
        if (pattern.test(value)) {
          throw new Error(
            `🔴 SECURITY ERROR: Possible API key detected in config at ${path}. ` +
            `All API calls must go through SecureAPI. ` +
            `Found: ${value.substring(0, 20)}...`
          );
        }
      }
    } else if (typeof value === 'object' && value !== null) {
      // Recursively check nested objects
      for (const [key, val] of Object.entries(value)) {
        checkValue(val, path ? `${path}.${key}` : key);
      }
    }
  };
  
  checkValue(config);
  console.log('✅ Config validation passed - no exposed API keys detected');
  return true;
}

/**
 * Development-only helper to show current configuration
 * DO NOT use in production
 */
export function debugConfig() {
  if (process.env.NODE_ENV === 'production') {
    console.warn('⚠️ debugConfig() should not be called in production');
    return;
  }
  
  console.log('🔧 Current Secure Configuration:');
  console.table({
    'Azure OpenAI Endpoint': AZURE_OPENAI_ENDPOINT,
    'Azure OpenAI Key': AZURE_OPENAI_API_KEY,
    'Azure OpenAI Deployment': AZURE_OPENAI_DEPLOYMENT,
    'Azure Speech Key': AZURE_SPEECH_KEY,
    'Azure Speech Region': AZURE_SPEECH_REGION,
    'Hume API Key': HUME_API_KEY
  });
  
  console.log('✅ All values should be "SECURE_API"');
  console.log('✅ Real keys are stored in Firebase Cloud Functions');
  
  try {
    validateSecureConfig(getAllSecureConfigs());
  } catch (error) {
    console.error(error.message);
  }
}